import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';

/**
 * Weather data interface matching the database schema
 */
export interface WeatherData {
  date: string; // ISO date string (YYYY-MM-DD)
  latitude: number;
  longitude: number;
  temperature_max: number | null;
  temperature_min: number | null;
  temperature_avg: number | null;
  condition: string | null;
  weather_code: number | null;
}

/**
 * Open-Meteo API response structure
 */
interface OpenMeteoResponse {
  latitude: number;
  longitude: number;
  daily: {
    time: string[];
    temperature_2m_max: number[];
    temperature_2m_min: number[];
    temperature_2m_mean: number[];
    weather_code: number[];
  };
}

/**
 * Map Open-Meteo weather codes to human-readable conditions
 * Based on WMO Weather interpretation codes
 */
function mapWeatherCodeToCondition(code: number): string {
  if (code === 0) return 'Clear';
  if (code >= 1 && code <= 3) return 'Cloudy';
  if (code >= 45 && code <= 48) return 'Foggy';
  if (code >= 51 && code <= 57) return 'Drizzle';
  if (code >= 61 && code <= 67) return 'Rainy';
  if (code >= 71 && code <= 77) return 'Snowy';
  if (code >= 80 && code <= 82) return 'Showers';
  if (code >= 85 && code <= 86) return 'Snow Showers';
  if (code >= 95 && code <= 99) return 'Stormy';
  return 'Cloudy';
}

/**
 * Fetch weather data from database for given dates and location
 * Returns a map of date string to weather data
 */
export async function getWeatherFromDatabase(
  dates: Date[],
  latitude: number,
  longitude: number
): Promise<Map<string, WeatherData>> {
  const dateStrings = dates.map(d => format(d, 'yyyy-MM-dd'));

  // Round coordinates to 6 decimal places to match storage precision
  const roundedLat = parseFloat(latitude.toFixed(6));
  const roundedLon = parseFloat(longitude.toFixed(6));

  const { data, error } = await supabase
    .from('weather_data')
    .select('*')
    .in('date', dateStrings)
    .eq('latitude', roundedLat)
    .eq('longitude', roundedLon);

  if (error) {
    console.error('Error fetching weather data from database:', error);
    return new Map();
  }

  const weatherMap = new Map<string, WeatherData>();
  data?.forEach(row => {
    weatherMap.set(row.date, row);
  });

  return weatherMap;
}

/**
 * Fetch historical weather data from Open-Meteo Archive API
 * Note: This is for past dates only. For current/future dates, use forecast API
 */
async function fetchWeatherFromOpenMeteo(
  startDate: Date,
  endDate: Date,
  latitude: number,
  longitude: number
): Promise<OpenMeteoResponse | null> {
  const startDateStr = format(startDate, 'yyyy-MM-dd');
  const endDateStr = format(endDate, 'yyyy-MM-dd');

  const url = new URL('https://archive-api.open-meteo.com/v1/archive');
  url.searchParams.append('latitude', latitude.toFixed(6));
  url.searchParams.append('longitude', longitude.toFixed(6));
  url.searchParams.append('start_date', startDateStr);
  url.searchParams.append('end_date', endDateStr);
  url.searchParams.append('daily', 'temperature_2m_max,temperature_2m_min,temperature_2m_mean,weather_code');
  url.searchParams.append('timezone', 'auto');

  try {
    const response = await fetch(url.toString());

    if (!response.ok) {
      console.error('Open-Meteo API error:', response.status, response.statusText);
      return null;
    }

    const data: OpenMeteoResponse = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching weather from Open-Meteo:', error);
    return null;
  }
}

/**
 * Store weather data in the database
 * Uses ON CONFLICT to handle duplicate date/location combinations
 */
async function storeWeatherInDatabase(
  weatherData: WeatherData[],
  businessId: string | null = null
): Promise<void> {
  if (weatherData.length === 0) return;

  // Prepare data for insertion with business_id
  const dataToInsert = weatherData.map(w => ({
    ...w,
    business_id: businessId
  }));

  const { error } = await supabase
    .from('weather_data')
    .upsert(dataToInsert, {
      onConflict: 'date,latitude,longitude',
      ignoreDuplicates: true
    });

  if (error) {
    console.error('Error storing weather data:', error);
  } else {
    console.log(`Stored weather data for ${weatherData.length} dates`);
  }
}

/**
 * Main function: Fetch and store weather data for multiple dates
 *
 * This function:
 * 1. Checks database for existing weather data
 * 2. Identifies missing dates
 * 3. Fetches missing data from Open-Meteo API
 * 4. Stores fetched data in database
 * 5. Returns complete weather data map
 *
 * @param dates - Array of dates to fetch weather for
 * @param latitude - Latitude of location (from business address)
 * @param longitude - Longitude of location (from business address)
 * @param businessId - Optional business ID for tracking
 * @returns Map of date string (YYYY-MM-DD) to weather data
 */
export async function fetchAndStoreWeatherForDates(
  dates: Date[],
  latitude: number,
  longitude: number,
  businessId: string | null = null
): Promise<Map<string, WeatherData>> {
  if (dates.length === 0) {
    return new Map();
  }

  // Round coordinates to 6 decimal places for consistency
  const roundedLat = parseFloat(latitude.toFixed(6));
  const roundedLon = parseFloat(longitude.toFixed(6));

  console.log(`[Weather Service] Fetching weather for ${dates.length} dates at (${roundedLat}, ${roundedLon})`);

  // Step 1: Check database for existing data
  const existingWeather = await getWeatherFromDatabase(dates, roundedLat, roundedLon);
  console.log(`[Weather Service] Found ${existingWeather.size} dates in database`);

  // Step 2: Identify missing dates
  const missingDates = dates.filter(date => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return !existingWeather.has(dateStr);
  });

  console.log(`[Weather Service] Need to fetch ${missingDates.length} dates from API`);

  // Step 3: Fetch missing data from API (if any)
  if (missingDates.length > 0) {
    // Sort dates to get range
    const sortedDates = [...missingDates].sort((a, b) => a.getTime() - b.getTime());
    const startDate = sortedDates[0];
    const endDate = sortedDates[sortedDates.length - 1];

    console.log(`[Weather Service] Fetching weather from ${format(startDate, 'yyyy-MM-dd')} to ${format(endDate, 'yyyy-MM-dd')}`);

    const apiData = await fetchWeatherFromOpenMeteo(startDate, endDate, roundedLat, roundedLon);

    if (apiData && apiData.daily) {
      // Parse API response into WeatherData objects
      const newWeatherData: WeatherData[] = apiData.daily.time.map((dateStr, index) => ({
        date: dateStr,
        latitude: roundedLat,
        longitude: roundedLon,
        temperature_max: Math.round(apiData.daily.temperature_2m_max[index]),
        temperature_min: Math.round(apiData.daily.temperature_2m_min[index]),
        temperature_avg: Math.round(apiData.daily.temperature_2m_mean[index]),
        weather_code: apiData.daily.weather_code[index],
        condition: mapWeatherCodeToCondition(apiData.daily.weather_code[index])
      }));

      // Step 4: Store in database
      await storeWeatherInDatabase(newWeatherData, businessId);

      // Add to result map
      newWeatherData.forEach(data => {
        existingWeather.set(data.date, data);
      });

      console.log(`[Weather Service] Successfully fetched and stored ${newWeatherData.length} weather records`);
    } else {
      console.warn('[Weather Service] Failed to fetch weather data from Open-Meteo API');
    }
  }

  return existingWeather;
}

/**
 * Weather display data structure for UI components
 */
export interface WeatherDisplayData {
  condition: string;
  icon: string;
  temperatureMin: number;
  temperatureMax: number;
  temperatureAvg: number;
}

/**
 * Map weather code to icon identifier (matching WeatherTimeInfoCard)
 */
function mapWeatherCodeToIcon(code: number): string {
  if (code === 0) return 'sun';
  if (code >= 1 && code <= 3) return 'cloud';
  if (code >= 45 && code <= 48) return 'cloud';
  if (code >= 51 && code <= 57) return 'drizzle';
  if (code >= 61 && code <= 67) return 'rain';
  if (code >= 71 && code <= 77) return 'snow';
  if (code >= 80 && code <= 82) return 'showers';
  if (code >= 85 && code <= 86) return 'snow';
  if (code >= 95 && code <= 99) return 'storm';
  return 'cloud';
}

/**
 * Format weather data for display
 * Returns structured data for forecast-style display
 */
export function formatWeatherForDisplay(weather: WeatherData | undefined): WeatherDisplayData | null {
  if (!weather) return null;

  return {
    condition: weather.condition || 'Unknown',
    icon: mapWeatherCodeToIcon(weather.weather_code || 0),
    temperatureMin: weather.temperature_min ?? 0,
    temperatureMax: weather.temperature_max ?? 0,
    temperatureAvg: weather.temperature_avg ?? 0
  };
}
